import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { useToast } from "@/components/ui/use-toast"
import { Calendar, Clock, MapPin, Users, Palette, Download, RefreshCw, ImageIcon, Sun, Aperture, PenTool, Pencil, Box } from "lucide-react"
import { cn } from "@/lib/utils"

interface EventDetails {
  title: string
  date: string
  time: string
  location: string
  description: string
  expectedAttendees: string
}

interface ImageStyle {
  id: string
  name: string
  description: string
  category: "realistic_image" | "digital_illustration"
  variant?: string
  icon: JSX.Element
}

export const imageStyles: ImageStyle[] = [
  {
    id: "realistic_image",
    name: "Realistic Photo",
    description: "Professional, realistic photography style",
    category: "realistic_image",
    icon: <ImageIcon className="h-5 w-5 text-blue-500" />
  },
  {
    id: "realistic_image/natural_light",
    name: "Natural Light",
    description: "Soft, natural lighting for authentic feel",
    category: "realistic_image",
    variant: "natural_light",
    icon: <Sun className="h-5 w-5 text-yellow-500" />
  },
  {
    id: "realistic_image/studio_portrait",
    name: "Studio Style",
    description: "Professional studio quality look",
    category: "realistic_image",
    variant: "studio_portrait",
    icon: <Aperture className="h-5 w-5 text-purple-500" />
  },
  {
    id: "digital_illustration/2d_art_poster",
    name: "2D Art Poster",
    description: "Modern, artistic poster design",
    category: "digital_illustration",
    variant: "2d_art_poster",
    icon: <PenTool className="h-5 w-5 text-green-500" />
  },
  {
    id: "digital_illustration/hand_drawn",
    name: "Hand Drawn",
    description: "Artistic hand-drawn illustration style",
    category: "digital_illustration",
    variant: "hand_drawn",
    icon: <Pencil className="h-5 w-5 text-orange-500" />
  },
  {
    id: "digital_illustration/handmade_3d",
    name: "3D Style",
    description: "Modern 3D illustration look",
    category: "digital_illustration",
    variant: "handmade_3d",
    icon: <Box className="h-5 w-5 text-indigo-500" />
  }
]

export const aspectRatios = [
  { id: "1:1", name: "Square", description: "Perfect for Instagram posts", dimensions: "1080x1080" },
  { id: "16:9", name: "Wide", description: "Ideal for event banners", dimensions: "1920x1080" },
  { id: "9:16", name: "Story", description: "Great for Instagram/Facebook stories", dimensions: "1080x1920" },
  { id: "4:5", name: "Portrait", description: "Optimized for Instagram feed", dimensions: "1080x1350" }
]

export default function CommunityEventPromotionImagesPage({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<string[]>([])
  const [currentImage, setCurrentImage] = useState<string | null>(null)
  const [selectedStyle, setSelectedStyle] = useState("")
  const [selectedRatio, setSelectedRatio] = useState("1:1")
  const [regeneratingIndex, setRegeneratingIndex] = useState<number | null>(null)

  const [eventDetails, setEventDetails] = useState<EventDetails>({
    title: "",
    date: "",
    time: "",
    location: "",
    description: "",
    expectedAttendees: ""
  })

  const generatePrompt = (style: ImageStyle, details: EventDetails) => {
    return `Create a promotional image for a community event. 
    The event "${details.title}" will be held on ${details.date} at ${details.time} in ${details.location}. 
    ${details.description}. Expected attendance: ${details.expectedAttendees}. Include modern design elements, typography, settings, location, props, people, and ensure the image is suitable for social media promotion.`
  }

  const handleGenerate = async () => {
    if (!selectedStyle || !eventDetails.title || !eventDetails.description) {
      toast({
        title: "Missing information",
        description: "Please provide event title, description, and select a style",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    try {
      const style = imageStyles.find(s => s.id === selectedStyle)!
      const prompt = generatePrompt(style, eventDetails)

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          prompt,
          size: selectedRatio,
          style: selectedStyle,
          imageType: imageStyles.find(s => s.id === selectedStyle)?.category || "realistic_image"
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate images')
      }

      const data = await response.json()
      setCurrentImage(data.data.imageUrl)
      setGeneratedImages(prev => [...prev, data.data.imageUrl])
    } catch (error) {
      toast({
        title: "Error generating images",
        description: "Please try again later",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async (index: number) => {
    setRegeneratingIndex(index)
    try {
      const style = imageStyles.find(s => s.id === selectedStyle)!
      const prompt = generatePrompt(style, eventDetails)

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          prompt,
          size: selectedRatio,
          style: selectedStyle,
          imageType: imageStyles.find(s => s.id === selectedStyle)?.category || "realistic_image"
        })
      })

      if (!response.ok) {
        throw new Error('Failed to regenerate image')
      }

      const data = await response.json()
      const newImages = [...generatedImages]
      newImages[index] = data.data.imageUrl
      setGeneratedImages(newImages)
    } catch (error) {
      toast({
        title: "Error regenerating image",
        description: "Please try again later",
        variant: "destructive"
      })
    } finally {
      setRegeneratingIndex(null)
    }
  }

  const handleDownload = async (url: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `event-promotion-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      toast({
        title: "Error downloading image",
        description: "Please try again later",
        variant: "destructive"
      })
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Event Promotion Images</h2>
        <p className="text-muted-foreground">
          Create eye-catching visuals for your community events
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Event Details</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Event Title</Label>
                <Input
                  id="title"
                  value={eventDetails.title}
                  onChange={(e) => setEventDetails({ ...eventDetails, title: e.target.value })}
                  placeholder="Enter your event title"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                    <Input
                      id="date"
                      type="date"
                      value={eventDetails.date}
                      onChange={(e) => setEventDetails({ ...eventDetails, date: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="time">Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                    <Input
                      id="time"
                      type="time"
                      value={eventDetails.time}
                      onChange={(e) => setEventDetails({ ...eventDetails, time: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <Input
                    id="location"
                    value={eventDetails.location}
                    onChange={(e) => setEventDetails({ ...eventDetails, location: e.target.value })}
                    placeholder="Event location"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="expectedAttendees">Expected Attendees</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <Input
                    id="expectedAttendees"
                    value={eventDetails.expectedAttendees}
                    onChange={(e) => setEventDetails({ ...eventDetails, expectedAttendees: e.target.value })}
                    placeholder="e.g., 50-100 people"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Event Description</Label>
                <Textarea
                  id="description"
                  value={eventDetails.description}
                  onChange={(e) => setEventDetails({ ...eventDetails, description: e.target.value })}
                  placeholder="Describe your event and its impact"
                  className="mt-1.5 min-h-[100px]"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Image Style</h3>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {imageStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={cn(
                    "flex flex-col items-center p-4 rounded-lg border text-center transition-colors hover:bg-gray-50",
                    selectedStyle === style.id ? "border-primary ring-2 ring-primary/20" : ""
                  )}
                >
                  {style.icon}
                  <div className="mt-2 font-medium">{style.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{style.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Image Size</h3>
            <RadioGroup
              value={selectedRatio}
              onValueChange={setSelectedRatio}
              className="grid grid-cols-2 gap-4"
            >
              {aspectRatios.map((ratio) => (
                <div key={ratio.id}>
                  <RadioGroupItem value={ratio.id} id={ratio.id} className="peer sr-only" />
                  <Label
                    htmlFor={ratio.id}
                    className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-gray-50 peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                  >
                    <span className="font-medium">{ratio.name}</span>
                    <span className="text-xs text-muted-foreground mt-1">{ratio.dimensions}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Button
            onClick={handleGenerate}
            className="w-full"
            size="lg"
          >
            Generate Images
          </Button>
        </Card>

        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Generated Images</h3>
          {generatedImages.length > 0 ? (
            <div className="grid grid-cols-2 gap-4">
              {generatedImages.map((image, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="relative group">
                    <img
                      src={image}
                      alt={`Generated event promotion ${index + 1}`}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRegenerate(index)}
                        disabled={regeneratingIndex === index}
                      >
                        {regeneratingIndex === index ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownload(image)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 flex flex-col items-center justify-center text-center text-muted-foreground">
              <ImageIcon className="h-12 w-12 mb-4" />
              <p>Your generated images will appear here</p>
              <p className="text-sm">Fill in the event details and click generate to create promotional images</p>
            </Card>
          )}
        </div>
      </div>

      <LoadingDialog
        open={loading}
        title="Generating Images"
        description="Creating eye-catching visuals for your event..."
      />
    </div>
  )
}